import { type BaseEvent } from '@ag-ui/core';
import { skipToken, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { Canvases } from '@air/api';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { sanitizeMediaMap } from '../../sanitizeMediaMap';
import { replayEventsToMessages } from './replayEventsToMessages';
import { type CanvasThreadWithHistory } from './types';

const HTTP_STATUS_NOT_FOUND = 404;
const DEFAULT_MESSAGES: CanvasThreadWithHistory['messages'] = [];
const DEFAULT_STATE: CanvasThreadWithHistory['state'] = undefined;

export const PRIVATE_CANVAS_THREAD_KEY = 'PRIVATE_CANVAS_THREAD';

// z.custom<BaseEvent>() — trusts inner event shape, only validates the outer envelope.
const baseEventSchema = z.custom<BaseEvent>();

const agentStateSchema = z.looseObject({
  sessionSummary: z.string().optional(),
  sessionTitle: z.string().optional(),
  workspaceId: z.string().optional(),
});

const legacyResponseSchema = z.array(baseEventSchema);

const newResponseSchema = z.object({
  messages: z.array(baseEventSchema),
  state: agentStateSchema.optional(),
});

export const threadResponseSchema = z.union([newResponseSchema, legacyResponseSchema]);

export type PrivateCanvasThreadParams = {
  workspaceId: string | undefined;
  canvasId: string | undefined;
  id: string | undefined;
};

export const getPrivateCanvasThreadKey = (params: PrivateCanvasThreadParams) => {
  return [PRIVATE_CANVAS_THREAD_KEY, params];
};

export type UsePrivateCanvasThreadOptions = {
  enabled?: boolean;
};

/** Fetches a canvas thread with its full history (messages + agent state). See CLAUDE.md. */
export const usePrivateCanvasThread = (params: PrivateCanvasThreadParams, options?: UsePrivateCanvasThreadOptions) => {
  const { workspaceId, canvasId, id } = params;

  return useQuery({
    enabled: !!workspaceId && !!canvasId && !!id && (options?.enabled ?? true),
    queryFn:
      workspaceId && canvasId && id
        ? async (): Promise<CanvasThreadWithHistory> => {
            const thread = await callAirApi(() => Canvases.Threads.get({ canvasId, id, workspaceId }));

            // The thread's message history lives in S3 as a JSON file. The API
            // returns a presigned S3 URL pointing to the message history.
            if (!thread.messagesUrl) {
              return { ...thread, messages: [] };
            }

            const res = await fetch(thread.messagesUrl);

            // A 404 means the messages file doesn't exist yet (e.g. the thread
            // was just created and no messages have been persisted to S3). This
            // is expected and not an error — we treat it as an empty history.
            if (res.status === HTTP_STATUS_NOT_FOUND) {
              return { ...thread, messages: [] };
            }

            if (!res.ok) {
              throw new Error(`Failed to fetch thread messages: ${res.status}`);
            }

            const json: unknown = await res.json();
            const result = threadResponseSchema.safeParse(json);

            if (!result.success) {
              reportErrorToBugsnag({
                context: 'Unexpected thread history format from S3',
                error: result.error,
              });

              return { ...thread, messages: DEFAULT_MESSAGES, state: DEFAULT_STATE };
            }

            const body = result.data;

            const events = Array.isArray(body) ? body : body.messages;
            const state = Array.isArray(body) ? DEFAULT_STATE : (body.state ?? DEFAULT_STATE);

            const sanitizedState = state?.mediaMap ? { ...state, mediaMap: sanitizeMediaMap(state.mediaMap) } : state;

            const messages = await replayEventsToMessages(events);

            return { ...thread, messages, state: sanitizedState };
          }
        : skipToken,
    queryKey: getPrivateCanvasThreadKey(params),
    staleTime: Infinity,
  });
};
